import Axios from 'axios';
import { isDomainRu } from '@/_helpers/utils.js';

const testEnvironments = ['localhost:8080', 'test01.vcv.team', 'test02.vcv.team', 'test03.vcv.team', 'test04.vcv.team'];

let integrationHost = process.env.INTEGRATION_HOST;

if (testEnvironments.includes(window.location.host)) {
  integrationHost = process.env.INTEGRATION_HOST_TEST;
}

if (!isDomainRu) {
  integrationHost = process.env.INTEGRATION_HOST_AI;
}

const getDefaultState = () => ({
  isHeadhunterLoading: false,
  headhunterSearchVacancy: [],
  headhunterSearchFieldsGrouped: [],
  headhunterSearchFields: [],
  headhunterTimezones: [],
  headhunterTestSearch: null
});

const state = getDefaultState();

const getters = {
  getTimezoneByKey: state => timezoneKey => {
    const timezone = state.headhunterTimezones.find(x => x.id === timezoneKey);

    if (timezone !== undefined) {
      return timezone;
    } else {
      return state.headhunterTimezones[0];
    }
  },

  getHeadhunterAutosearchFieldProperties: state => fieldId => {
    const field = state.headhunterSearchFields.find(x => x.id === fieldId);

    if (field !== undefined) {
      return field;
    }

    return null;
  },

  getDictionaryObjectForSelect: state => (fieldId, fieldValue) => {
    const field = state.headhunterSearchFields.find(x => x.id === fieldId);

    if (field !== undefined) {
      if (field.dictionary !== null && field.dictionary !== undefined && fieldValue !== null) {
        return field.dictionary.find(x => x.external_id === fieldValue);
      }
    }

    return null;
  }
};

const mutations = {
  SET_IS_HEADHUNTER_LOADING: (state, payload) => {
    state.isHeadhunterLoading = payload;
  },

  SET_HEADHUNTER_SEARCH_VACANCY: (state, vacancies) => {
    state.headhunterSearchVacancy = vacancies;
  },

  SET_HEADHUNTER_SEARCH_FIELDS_GROUPED: (state, fields) => {
    state.headhunterSearchFieldsGrouped = fields;
  },

  SET_HEADHUNTER_SEARCH_FIELDS: (state, fields) => {
    state.headhunterSearchFields = fields;
  },

  SET_HEADHUNTER_TIMEZONES: (state, zones) => {
    state.headhunterTimezones = zones;
  },

  SET_HEADHUNTER_TEST_SEARCH: (state, payload) => {
    state.headhunterTestSearch = payload;
  },

  RESET_INTEGRATIONS_HEADHUNTER: state => {
    Object.assign(state, getDefaultState());
  }
};

const actions = {
  checkAttachedVacancy({ commit, rootGetters }, vcvVacancyId) {
    commit('SET_IS_HEADHUNTER_LOADING', true);
    const params = rootGetters['integrations/getIntegrationParams']('headhunter');

    if (params.access_token) {
      return Axios.get(
        `${integrationHost}/v1/headhunter/search-vacancies?filter[vcv_vacancy_id]=${vcvVacancyId}&with[]=values`,
        { headers: { Authorization: `Bearer ${params.access_token}` } }
      )
        .then(response => {
          if (response.data._embedded.vacancies.length > 0) {
            commit('SET_HEADHUNTER_SEARCH_VACANCY', response.data._embedded.vacancies[0]);

            return response.data._embedded.vacancies[0];
          }

          return null;
        })
        .finally(() => {
          commit('SET_IS_HEADHUNTER_LOADING', false);
        });
    }
  },

  async saveAutosearch({ commit, state, rootGetters }, payload) {
    commit('SET_IS_HEADHUNTER_LOADING', true);
    const params = rootGetters['integrations/getIntegrationParams']('headhunter');

    if (params.access_token) {
      // Если нет связанных вакансий, то post
      if (state.headhunterSearchVacancy === null || state.headhunterSearchVacancy.length === 0) {
        await Axios.post(`${integrationHost}/v1/headhunter/search-vacancies?with[]=values`, payload, {
          headers: { Authorization: `Bearer ${params.access_token}` }
        })
          .then(response => {
            commit('SET_HEADHUNTER_SEARCH_VACANCY', response.data);

            if (response.status === 200 || response.status === 201) {
              this._vm.$noty.success(window.$tr('settings.security.save_success'));
            }
          })
          .catch(error => {
            if (error.response !== undefined && error.response.status === 400) {
              this._vm.$noty.error('Одно или несколько полей в форме не заполнено или заполнено неправильно');
            }
          })
          .finally(() => {
            commit('SET_IS_HEADHUNTER_LOADING', false);
          });
      } else {
        // Если вакансия уже связана с интеграцией, то делаем patch
        await Axios.patch(
          `${integrationHost}/v1/headhunter/search-vacancies/${state.headhunterSearchVacancy.id}?with[]=values`,
          payload,
          { headers: { Authorization: `Bearer ${params.access_token}` } }
        )
          .then(response => {
            commit('SET_HEADHUNTER_SEARCH_VACANCY', response.data._embedded.vacancies[0]);

            if (response.status === 200 || response.status === 201) {
              this._vm.$noty.success(window.$tr('settings.security.save_success'));
            }
          })
          .catch(error => {
            if (error.response !== undefined && error.response.status === 400) {
              this._vm.$noty.error('Одно или несколько полей в форме не заполнено или заполнено неправильно');
            }
          })
          .finally(() => {
            commit('SET_IS_HEADHUNTER_LOADING', false);
          });
      }
    }
  },

  fetchFormFields({ commit, rootGetters }) {
    commit('SET_IS_HEADHUNTER_LOADING', true);
    const params = rootGetters['integrations/getIntegrationParams']('headhunter');

    if (params.access_token) {
      Axios.get(`${integrationHost}/v1/headhunter/search-fields?with[]=dictionary&limit=250`, {
        headers: { Authorization: `Bearer ${params.access_token}` }
      }).then(response => {
        commit('SET_HEADHUNTER_SEARCH_FIELDS', response.data._embedded.fields);
      });

      return Axios.get(
        `${integrationHost}/v1/headhunter/search-field-groups?with[]=fields&with[]=fields.dictionary&limit=250`,
        { headers: { Authorization: `Bearer ${params.access_token}` } }
      )
        .then(response => {
          const groups = response.data._embedded.groups;

          commit('SET_HEADHUNTER_SEARCH_FIELDS_GROUPED', groups);

          const formFields = {};

          for (let i = 0; i < groups.length; i++) {
            const fields = groups[i]._embedded.fields;

            for (let u = 0; u < fields.length; u++) {
              const propertyName = fields[u].id;

              if (fields[u].type === 'checkbox') {
                formFields[propertyName] = false;
              } else if (fields[u].type === 'suggest') {
                formFields[propertyName] = [];
              } else if (fields[u].type === 'string' || fields[u].type === 'num') {
                formFields[propertyName] = '';
              } else {
                formFields[propertyName] = null;
              }
            }
          }

          return formFields;
        })
        .finally(() => {
          commit('SET_IS_HEADHUNTER_LOADING', false);
        });
    }
  },

  fetchTimeZones({ commit, rootGetters }) {
    commit('SET_IS_HEADHUNTER_LOADING', true);
    const params = rootGetters['integrations/getIntegrationParams']('headhunter');

    if (params.access_token) {
      Axios.get(`${integrationHost}/v1/headhunter/timezones`, {
        headers: { Authorization: `Bearer ${params.access_token}` }
      })
        .then(response => {
          let zonesArray = [];
          const zones = Object.entries(response.data);

          zones.forEach(([key, value]) => {
            zonesArray.push({ id: key, name: value });
          });

          commit('SET_HEADHUNTER_TIMEZONES', zonesArray);
        })
        .finally(() => {
          commit('SET_IS_HEADHUNTER_LOADING', false);
        });
    }
  },

  async resetAutosearchStatistic({ state, commit, rootGetters }) {
    commit('SET_IS_HEADHUNTER_LOADING', true);
    const params = rootGetters['integrations/getIntegrationParams']('headhunter');

    if (params.access_token) {
      await Axios.post(
        `${integrationHost}/v1/headhunter/search-vacancies/${state.headhunterSearchVacancy.id}/reset-limit`,
        {},
        { headers: { Authorization: `Bearer ${params.access_token}` } }
      ).finally(() => {
        commit('SET_IS_HEADHUNTER_LOADING', false);
      });
    }
  },

  async fetchSuggest({ commit, rootGetters }, suggest) {
    commit('SET_IS_HEADHUNTER_LOADING', true);
    const params = rootGetters['integrations/getIntegrationParams']('headhunter');

    if (params.access_token) {
      return await Axios.get(`${integrationHost}/v1/headhunter/suggest/${suggest.code}?name=${suggest.value}`, {
        headers: { Authorization: `Bearer ${params.access_token}` }
      })
        .then(response => {
          return response.data;
        })
        .finally(() => {
          commit('SET_IS_HEADHUNTER_LOADING', false);
        });
    }
  },

  async fetchTestSearch({ state, commit, rootGetters }) {
    commit('SET_IS_HEADHUNTER_LOADING', true);
    const params = rootGetters['integrations/getIntegrationParams']('headhunter');

    await Axios.get(`${integrationHost}/v1/headhunter/search-vacancies/${state.headhunterSearchVacancy.id}/preview`, {
      headers: { Authorization: `Bearer ${params.access_token}` }
    })
      .then(response => {
        commit('SET_HEADHUNTER_TEST_SEARCH', response.data.resumes);
      })
      .finally(() => {
        commit('SET_IS_HEADHUNTER_LOADING', false);
      });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
